import type { StagedAction } from '@/lib/engine/stage-action';
import type { ActionItem } from '@/lib/queries/types';
import { isConversationActionType } from '@/lib/actions/conversation-cards';

/**
 * The approval card Mucka promised, recovered from the durable row.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 *
 * A money/email tool asked for IN THE APP is staged rather than sent, and the
 * agent is told to say so: *"Ready below … tell the user to hit Send."* Two
 * things then have to happen — the row goes into `engine_actions` as
 * `rule_type = 'chat_staged'`, and a `stagedAction` travels back to the panel
 * so the button is drawn. The row is durable. **The delivery was not, and on
 * one whole surface it never existed at all.**
 *
 * On the standard in-app ElevenLabs agent the tool runs over MCP
 * (`/api/[transport]`), which forwards `mutation_summary`, `mutation_entity`,
 * `view_path` and `nav_action` to the model and DROPS `result.stagedAction` —
 * there is nowhere for it to go, because MCP answers ElevenLabs, not the
 * browser. `VoiceEngine`'s `pendingStagedRef` was declared, cleared and read,
 * and nothing ever wrote it. So the agent insisted on a button that could not
 * be drawn, and then argued with the owner about it:
 *
 *   A heating firm's owner, 21 Sep 06:38 (TKT-1310)
 *     Mucka: "There's an approval card just below — give it a quick check
 *             and hit Send to fire it off to [the customer]."
 *     Owner: "There's no approval card."
 *     Mucka: "It should be on your invoices page … try a quick refresh."
 *
 *   Another heating firm, 21 Sep 09:38 (TKT-1313), own-brain —
 *   a path that HAS a delivery route, and still lost it.
 *     Engineer: "There's still no send button on the screen, Mucka."
 *
 * ── Why recovery rather than a fifth delivery path ───────────────────────────
 *
 * Every engine already writes the row. Reading it back is the one mechanism
 * that is true of the ElevenLabs agent, the own-brain agent and typed chat at
 * once, and it is the only one that survives the event being dropped in
 * flight — which is what TKT-1196 recorded (eight valid approvals reached
 * `engine_actions`, none rendered) and what TKT-1313 looks like. The live
 * `stagedAction` stays the fast path; this is the backstop behind it.
 *
 * 🚨 `chat_staged` ONLY. `phone_staged` is a card raised on a phone call,
 * where the deliberate design is that it waits in Actions for somebody
 * authenticated to approve at a screen. Pulling one into the chat would put a
 * spoofable caller's request under a Send button in the owner's panel.
 */

/** How old a pending card may be and still be treated as "just prepared".
 *
 *  The fault being fixed is a card promised in THIS conversation that never
 *  arrived, and a reload mid-call has to keep working (the owner was told to
 *  refresh, and did). Anything older has been left rather than missed, and is
 *  left to expire: a card raised in a conversation is on no Actions list
 *  (`narrowToVisibleCards` rule 4), and asking again is one sentence. Cards are expired nightly, so this window is
 *  the only thing between "recovered" and "yesterday's business reappearing
 *  under today's reply". */
export const RECOVERABLE_STAGED_AGE_MS = 2 * 60 * 60 * 1000;

/**
 * How often to re-read the pending cards while a voice call is live.
 *
 * Realtime is the ordinary path and this never fires on top of a working one —
 * it exists for the session where realtime does not deliver, which is the
 * session where somebody is on a call being told to tap a button. Slow enough
 * to be invisible next to the call itself, fast enough that it lands inside
 * the same breath.
 */
export const STAGED_RECOVERY_POLL_MS = 5000;

/** The rows this may draw. Anything else is somebody else's surface. */
const RECOVERABLE_RULE_TYPE = 'chat_staged';


/** A rendered chat message, as far as this needs to know. */
export interface StagedTarget {
  role: 'user' | 'mucka';
  stagedActions?: StagedAction[];
  /** When the message was added (ms). The earliest one is when this thread began. */
  time?: number;
}

/**
 * How far before the thread's first message a card may be dated and still be
 * this thread's: the card's `createdAt` is the server's clock and a message's
 * `time` is the device's, and the two drift.
 */
export const THREAD_START_SLACK_MS = 2 * 60 * 1000;

/**
 * The card's colour as the panel draws it.
 *
 * `ActionItem.tagColor` is typed for the inbox's palette and staging writes
 * `green` for a payment, so the stored value is genuinely outside that union.
 * Read the real value and fall back to the send colour rather than trusting
 * either type — a card with the wrong icon still sends the right email; a card
 * that throws does not appear at all.
 */
function stagedTagColour(raw: unknown): StagedAction['tagColor'] {
  return raw === 'green' || raw === 'orange' ? raw : 'blue';
}

/** The panel's card shape, from the durable row. */
export function stagedActionFromCard(action: ActionItem): StagedAction {
  return {
    id: action.id,
    tag: action.tag,
    tagColor: stagedTagColour((action as { tagColor?: unknown }).tagColor),
    body: action.body,
    primaryLabel: action.primaryLabel,
    primaryDoneLabel: action.primaryDoneLabel,
  };
}

/**
 * Which pending approval cards belong in the thread and are not in it yet.
 *
 * Ordered oldest first so two cards staged in one turn read in the order they
 * were prepared.
 *
 * `now` is a parameter so the age rule is testable without faking a clock. A
 * row with no `createdAt` is NOT recovered: a reader that does not select the
 * column cannot tell a fresh card from an old one, and guessing "recent" there
 * is how yesterday's approval ends up under today's reply.
 */
export function recoverableStagedCards(
  actions: readonly ActionItem[],
  messages: readonly StagedTarget[],
  now: number,
): StagedAction[] {
  const onScreen = new Set<string>();
  for (const m of messages) for (const sa of m.stagedActions ?? []) onScreen.add(sa.id);
  // 🚨 A card from BEFORE this thread began is not this thread's. Clearing the
  // chat and pressing the mic drew two email cards staged an hour earlier in
  // another conversation, under the new greeting (demo, 10 Oct 2026) — the age
  // window alone cannot tell "promised here and lost" from "left over there".
  let threadStart = Infinity;
  for (const m of messages) if (typeof m.time === 'number' && m.time < threadStart) threadStart = m.time;

  return actions
    .filter(a => a.ruleType === RECOVERABLE_RULE_TYPE && isConversationActionType(a.actionType))
    .filter(a => !onScreen.has(a.id))
    .filter(a => {
      if (!a.createdAt) return false;
      const at = Date.parse(a.createdAt);
      return Number.isFinite(at) && now - at <= RECOVERABLE_STAGED_AGE_MS && at - now <= RECOVERABLE_STAGED_AGE_MS
        && (threadStart === Infinity || at >= threadStart - THREAD_START_SLACK_MS);
    })
    .sort((x, y) => Date.parse(x.createdAt!) - Date.parse(y.createdAt!))
    .map(stagedActionFromCard);
}

/**
 * Hang recovered cards off the most recent Mucka reply.
 *
 * 🚨 The LAST MUCKA MESSAGE, not the last message — which is where this parts
 * company with `attachExtrasToLatestReply`. Chips are position-sensitive
 * (MuckaPanel only lets the newest set be tapped, so attaching an old set
 * steals taps from the live question); a staged card is not — the panel
 * renders each card once, at the newest message carrying its id, and the
 * button acts on that one row whenever it is pressed.
 *
 * The difference is the whole point here. This runs when the owner has ALREADY
 * said "there's no card" — so the last message is very often theirs, and a
 * rule that refused to attach in that case would refuse in exactly the
 * conversation it exists for.
 */
export function attachStagedToLastReply<T extends StagedTarget>(
  messages: T[],
  cards: readonly StagedAction[],
): { messages: T[]; attached: boolean } {
  if (!cards.length) return { messages, attached: false };
  let target = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'mucka') { target = i; break; }
  }
  if (target < 0) return { messages, attached: false };

  const seen = new Set((messages[target].stagedActions ?? []).map(a => a.id));
  const added = cards.filter(a => (seen.has(a.id) ? false : (seen.add(a.id), true)));
  if (!added.length) return { messages, attached: false };

  const next = messages.slice();
  next[target] = {
    ...messages[target],
    stagedActions: [...(messages[target].stagedActions ?? []), ...added],
  };
  return { messages: next, attached: true };
}
